import { useInfiniteQuery } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useState } from 'react';
import { actorLabel, formatChange, formatTime } from '../features/history/format.ts';

/** Organization audit log (requires `audit.view`): sign-ins, grants, environment and app changes. */
export function AuditPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/settings/audit' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/audit' });
  const [onlyThisEnvironment, setOnlyThisEnvironment] = useState(false);
  const filters = onlyThisEnvironment ? { environmentId } : {};
  const log = useInfiniteQuery({
    queryKey: ['audit', filters],
    queryFn: ({ pageParam }) => api.listAudit({ ...filters, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: '',
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
  const events = log.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <section>
      <h1>Audit log</h1>
      <label className="inline-form">
        <input type="checkbox" checked={onlyThisEnvironment} onChange={(e) => setOnlyThisEnvironment(e.target.checked)} />
        Only this environment
      </label>
      {log.isPending ? (
        <p aria-busy="true">Loading audit log…</p>
      ) : log.isError ? (
        <p role="alert">Audit log could not be loaded.</p>
      ) : events.length === 0 ? (
        <p className="empty-state">No events yet.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Time</th>
              <th scope="col">Actor</th>
              <th scope="col">Action</th>
              <th scope="col">Object</th>
              <th scope="col">Changes</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.id}>
                <td>
                  <time dateTime={event.occurredAt}>{formatTime(event.occurredAt)}</time>
                </td>
                <td>{actorLabel(event.actor)}</td>
                <td>
                  <code>{event.action}</code>
                </td>
                <td>{event.objectType ? `${event.objectType} ${event.objectId ?? ''}` : '—'}</td>
                <td>
                  {event.summary.map((change) => (
                    <div key={change.path}>
                      <code>{formatChange(change)}</code>
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {log.hasNextPage && (
        <button type="button" onClick={() => void log.fetchNextPage()} disabled={log.isFetchingNextPage}>
          Load more
        </button>
      )}
    </section>
  );
}
